import { generateClient } from 'aws-amplify/api'
import { sendMessage, type SendMessageInput } from './appsync-client'

/**
 * Media upload flow for chat attachments (S3 + AppSync + DynamoDB).
 *
 * 1. Request a pre-signed PUT URL from AppSync (`getAttachmentUploadUrl`).
 * 2. Upload the raw file directly to S3 with HTTP PUT (browser / Capacitor webview).
 * 3. Send the chat message via AppSync (`sendMessage`) with `attachmentUrl` + `attachmentType`.
 *
 * Large binaries never touch DynamoDB (400KB item limit). AppSync subscriptions deliver metadata only.
 */

const client = generateClient()

export type AttachmentType = 'IMAGE' | 'GIF' | 'VIDEO' | 'FILE' | 'VOICE'

export type GetAttachmentUploadUrlInput = {
  conversationId: string
  fileName: string
  contentType: string
  attachmentType: AttachmentType
  /** SEC-07: declared size — the PUT is signed with this exact ContentLength. */
  sizeBytes?: number
}

export type AttachmentUploadUrl = {
  uploadUrl: string
  attachmentUrl: string
  attachmentKey: string
  expiresAt: string
  expiresIn: number
}

export type UploadChatMediaParams = {
  conversationId: string
  file: File | Blob
  fileName: string
  contentType: string
  attachmentType: AttachmentType
  /** Optional caption stored in message body */
  caption?: string
}

export type UploadChatMediaResult = {
  attachmentUrl: string
  attachmentKey: string
  message: unknown
}

const getAttachmentUploadUrlQuery = /* GraphQL */ `
  query GetAttachmentUploadUrl($input: GetAttachmentUploadUrlInput!) {
    getAttachmentUploadUrl(input: $input) {
      uploadUrl
      attachmentUrl
      attachmentKey
      expiresAt
      expiresIn
    }
  }
`

/**
 * Step 1 — Ask AppSync (Lambda) for a secure S3 pre-signed PUT URL.
 */
export async function getAttachmentUploadUrl(
  input: GetAttachmentUploadUrlInput,
): Promise<AttachmentUploadUrl> {
  const result = await client.graphql({
    query: getAttachmentUploadUrlQuery,
    variables: { input },
  })

  if ('errors' in result && result.errors?.length) {
    throw new Error(
      result.errors.map((e: { message: string }) => e.message).join('; '),
    )
  }
  if (!('data' in result) || !result.data) {
    throw new Error('Empty GraphQL response')
  }

  return (result.data as { getAttachmentUploadUrl: AttachmentUploadUrl })
    .getAttachmentUploadUrl
}

/**
 * Step 2 — PUT the file bytes directly to S3 (must match signed Content-Type).
 */
export async function uploadFileToS3(
  uploadUrl: string,
  file: File | Blob,
  contentType: string,
): Promise<void> {
  let response: Response
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    })
  } catch (err) {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'this app origin'
    const detail = err instanceof Error ? err.message : String(err)
    throw new Error(
      `Image upload to storage failed (${detail}). ` +
        `If you are on the hosted demo site, run infrastructure/scripts/configure-demo-origin.sh ` +
        `so S3 allows uploads from ${origin}.`,
    )
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(
      `Image upload to storage failed (${response.status}): ${text || response.statusText}`,
    )
  }
}

/**
 * Step 3 — Persist message metadata in DynamoDB via AppSync (includes S3 URL).
 */
export async function sendMessageWithAttachment(
  input: SendMessageInput & {
    attachmentUrl: string
    attachmentType: AttachmentType
    messageType?: 'TEXT' | 'MEDIA' | 'SYSTEM'
  },
) {
  return sendMessage({
    ...input,
    messageType: input.messageType ?? 'MEDIA',
  })
}

/**
 * Upload a single attachment file to S3 (pre-signed URL → S3 PUT) WITHOUT
 * sending a chat message. Returns the S3 attachment URL/key so callers can
 * batch several uploads into one multi-image message.
 */
export async function uploadAttachmentFile(params: {
  conversationId: string
  file: File | Blob
  fileName: string
  contentType: string
  attachmentType: AttachmentType
}): Promise<{ attachmentUrl: string; attachmentKey: string }> {
  const presigned = await getAttachmentUploadUrl({
    conversationId: params.conversationId,
    fileName: params.fileName,
    contentType: params.contentType,
    attachmentType: params.attachmentType,
    sizeBytes: params.file.size,
  })
  await uploadFileToS3(presigned.uploadUrl, params.file, params.contentType)
  return {
    attachmentUrl: presigned.attachmentUrl,
    attachmentKey: presigned.attachmentKey,
  }
}

/**
 * End-to-end: pre-signed URL → S3 PUT → sendMessage mutation.
 */
export async function uploadChatMedia(
  params: UploadChatMediaParams,
): Promise<UploadChatMediaResult> {
  const { conversationId, file, fileName, contentType, attachmentType, caption } = params

  const { attachmentUrl, attachmentKey } = await uploadAttachmentFile({
    conversationId,
    file,
    fileName,
    contentType,
    attachmentType,
  })

  const messageResult = await sendMessageWithAttachment({
    conversationId,
    body: caption ?? '',
    attachmentUrl,
    attachmentType,
    messageType: 'MEDIA',
  })

  return {
    attachmentUrl,
    attachmentKey,
    message: messageResult,
  }
}

/** Map browser MIME type to GraphQL AttachmentType (extend as needed). */
export function inferAttachmentType(
  contentType: string,
  fileName: string,
): AttachmentType {
  if (contentType === 'image/gif' || fileName.toLowerCase().endsWith('.gif')) {
    return 'GIF'
  }
  if (contentType.startsWith('image/')) {
    return 'IMAGE'
  }
  if (contentType.startsWith('video/')) {
    return 'VIDEO'
  }
  if (contentType.startsWith('audio/')) {
    return 'VOICE'
  }
  return 'FILE'
}

/** Allowlist Giphy CDN hosts to avoid arbitrary URL fetch → S3 upload. */
export function isAllowedGifSourceUrl(gifUrl: string): boolean {
  try {
    const parsed = new URL(gifUrl)
    if (parsed.protocol !== 'https:') return false
    const host = parsed.hostname.toLowerCase()
    // media1…mediaN.giphy.com and other *.giphy.com hosts
    return host === 'giphy.com' || host.endsWith('.giphy.com')
  } catch {
    return false
  }
}

/** Fetch a remote GIF (Giphy CDN) and upload via the standard S3 + sendMessage flow. */
export async function uploadGifFromUrl(
  conversationId: string,
  gifUrl: string,
  caption?: string,
): Promise<UploadChatMediaResult> {
  if (!isAllowedGifSourceUrl(gifUrl)) {
    throw new Error('GIF URL must be from an allowlisted Giphy host')
  }
  const response = await fetch(gifUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch GIF (${response.status})`)
  }
  const blob = await response.blob()
  const contentType = blob.type || 'image/gif'
  if (contentType && contentType !== 'image/gif' && !contentType.startsWith('image/')) {
    throw new Error('GIF URL did not return an image')
  }
  return uploadChatMedia({
    conversationId,
    file: blob,
    fileName: 'animation.gif',
    contentType: contentType.startsWith('image/') ? contentType : 'image/gif',
    attachmentType: 'GIF',
    caption,
  })
}
